import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Heading,
  Text,
  Image,
  VStack,
  Badge,
  Button,
  HStack,
  Icon,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useParams, Link as RouterLink } from "react-router-dom";
import { FiShoppingBag, FiStar } from "react-icons/fi";
import { products } from "../data/products";
import { categories } from "../data/categories";
import { useCart } from "../context/CartContext";
import type { Product } from "../types";

const MotionBox = motion(Box);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const CategoryPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { addToCart } = useCart();
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const toast = useToast();

  const handleAddToCart = (product: Product) => {
    addToCart(product);
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart`,
      status: "success",
      duration: 2000,
      isClosable: true,
      position: "top",
      variant: "subtle",
    });
  };

  // Find the category details
  const category = categories.find((cat) => cat.id === categoryId);

  // Filter products by category
  const categoryProducts = products.filter(
    (product) => product.category === categoryId
  );

  if (!category) {
    return (
      <Container maxW="container.xl" py={8}>
        <Heading>Category not found</Heading>
      </Container>
    );
  }

  return (
    <Box py={8}>
      <Container maxW="1400px">
        <VStack spacing={8} align="stretch">
          <Heading textAlign="center" size="2xl">
            {category.name}
          </Heading>
          <Text textAlign="center" fontSize="lg" color="gray.600">
            {category.description}
          </Text>
          <Text textAlign="center" fontSize="md" color="gray.500">
            {categoryProducts.length} Products
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {categoryProducts.map((product) => (
              <MotionBox
                key={product.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Box
                  as={RouterLink}
                  to={`/product/${product.id}`}
                  cursor="pointer"
                  _hover={{ textDecoration: "none" }}
                >
                  <Box
                    borderWidth="1px"
                    borderRadius="lg"
                    overflow="hidden"
                    bg={bgColor}
                    shadow="md"
                    transition="all 0.3s"
                    _hover={{
                      shadow: "lg",
                      transform: "translateY(-4px)",
                    }}
                  >
                    <Image
                      src={product.image}
                      alt={product.name}
                      height="200px"
                      width="100%"
                      objectFit="cover"
                    />
                    <VStack p={4} align="start" spacing={2}>
                      <Heading size="md">{product.name}</Heading>
                      <Text color={textColor} noOfLines={2}>
                        {product.description}
                      </Text>
                      <HStack spacing={2}>
                        <Icon as={FiStar} color="yellow.400" />
                        <Text>{product.rating.toFixed(1)}</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Text
                          textDecoration="line-through"
                          color="green.300"
                          fontSize="sm"
                        >
                          Rs. {product.originalPrice.toFixed(2)}
                        </Text>
                        <Text fontSize="xl" fontWeight="bold" color="green.500">
                          Rs. {product.price.toFixed(2)}
                        </Text>
                        {product.discount > 0 && (
                          <Badge colorScheme="green">
                            {product.discount}% OFF
                          </Badge>
                        )}
                      </HStack>
                      <Button
                        leftIcon={<FiShoppingBag />}
                        colorScheme="green"
                        width="full"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddToCart(product);
                        }}
                      >
                        Add to Cart
                      </Button>
                    </VStack>
                  </Box>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default CategoryPage;
